import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { Listable } from 'listable'
import { Dialog } from 'frame/components'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'

import Row from './row'
import Filters from '../filters'
import ExceptionForm from '../item/form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { colors } from '@mui/material'

import Typography from '@mui/material/Typography'
import StyledModalV2 from 'frame/components/styled-modal-v2'
import { actions, config } from '../../module'
import {
  acknowledgeExceptions,
  saveExceptionNote,
  getExceptionNotes,
  updateExceptionNote,
  deleteExceptionNote
} from '../../actions'

import {
  hasCompleteExceptionIdentity,
  isSelectableStatus,
  parseExceptionKey,
  toExceptionKey
} from '../../common/exception-utils'

const List = ({
  account,
  model,
  onAcknowledge,
  onSetPending,
  onSaveNote,
  fetchNotes,
  onUpdateNote,
  onDeleteNote,
}) => {
  const [selectedItem, setSelectedItem] = useState(null)
  const [selectedExceptionKeys, setSelectedExceptionKeys] = useState([])
  const [ackDialogOpen, setAckDialogOpen] = useState(false)
  // Keep the open item in sync after acknowledge success.
  useEffect(() => {
    if (model?.flags?.acknowledge === 'processed') {
      setSelectedItem((item) =>
        item && model?.updatedExceptionKeys?.includes(toExceptionKey(item))
          ? { ...item, status: model.updatedExceptionStatus }
          : item
      )
    }
  }, [
    model?.flags?.acknowledge,
    model?.updatedExceptionKeys,
    model?.updatedExceptionStatus,
  ])

  // Clear selection and acknowledge dialog when pagination (page) changes
  useEffect(() => {
    if (model?.pagination && typeof model.pagination.offset !== 'undefined') {
      setSelectedExceptionKeys([])
      setAckDialogOpen(false)
    }
  }, [model?.pagination?.offset])

  const onClose = () => setSelectedItem(null)
  const closeHandlerRef = useRef(onClose)

  const handleDialogClose = useCallback(() => {
    closeHandlerRef.current()
  }, [])

  const handleRegisterCloseHandler = useCallback((handler) => {
    closeHandlerRef.current = handler
  }, [])

  // Toggle checkbox selection
  // Use composite key for selection
  const toggleSelect = (exceptionKey) => {
    if (!exceptionKey) {
      return
    }

    setSelectedExceptionKeys((prev) => {
      if (prev.includes(exceptionKey)) {
        return prev.filter((id) => id !== exceptionKey)
      }

      return [...prev, exceptionKey]
    })
  }
  const renderItem = useCallback(
    (item, index, itemProps) => {
      const onView = () => setSelectedItem(item)
      const exceptionKey = toExceptionKey(item)
      return (
        <Row
          key={exceptionKey}
          item={item}
          {...itemProps}
          onView={onView}
          checked={selectedExceptionKeys.includes(exceptionKey)}
          onToggleSelect={() => toggleSelect(exceptionKey)}
          exceptionKey={exceptionKey}/>
      )
    },
    [selectedExceptionKeys]
  )

  const pendingExceptionKeys = useMemo(() => {
    if (!Array.isArray(model?.list?.ids)) {
      return []
    }

    return model.list.ids
      .map((id) => model?.data?.[id])
      .filter(
        (item) =>
          item &&
          hasCompleteExceptionIdentity(item) &&
          isSelectableStatus(item.status)
      )
      .map(toExceptionKey)
  }, [model?.data, model?.list?.ids])

  const exceptionLookup = useMemo(() => {
    const lookup = new Map()

    if (Array.isArray(model?.ids)) {
      model.ids.forEach((id) => {
        const item = model?.data?.[id]
        if (item) {
          lookup.set(toExceptionKey(item), item)
        }
      })
    }

    if (Array.isArray(model?.list?.ids)) {
      model.list.ids.forEach((id) => {
        const item = model?.data?.[id]
        if (item) {
          lookup.set(toExceptionKey(item), item)
        }
      })
    }

    return lookup
  }, [model?.data, model?.ids, model?.list?.ids])

  const handleNavigateException = useCallback(
    (target) => {
      if (!target) {
        return
      }

      const key = toExceptionKey(target)
      const candidate = { ...exceptionLookup.get(key), ...target }
      setSelectedItem(candidate)
    },
    [exceptionLookup]
  )

  const allPendingSelected =
    pendingExceptionKeys.length > 0 &&
    pendingExceptionKeys.every((key) => selectedExceptionKeys.includes(key))

  const showLoader =
    model?.flags?.acknowledge === 'processing' &&
    model?.flags?.fetch !== 'processing'

  const handleSelectAll = () => {
    if (pendingExceptionKeys.length === 0) {
      return
    }

    const pendingSet = new Set(pendingExceptionKeys)

    setSelectedExceptionKeys((prev) => {
      if (allPendingSelected) {
        return prev.filter((key) => !pendingSet.has(key))
      }

      const combined = new Set([...prev, ...pendingSet])
      return Array.from(combined)
    })
  }

  const selectedCount = useMemo(
    () => new Set(selectedExceptionKeys).size,
    [selectedExceptionKeys]
  )

  return (
    <>
      <Listable
        actions={actions}
        config={config}
        filters={Filters}
        model={model}
        renderItem={renderItem}
        actionButtons={
          <Stack
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="flex-end"
            gap={1}>
            <Box width={{ xs: '100%', md: '200px' }}>
              <Button
                fullWidth
                variant="contained"
                color={allPendingSelected ? 'secondary' : 'primary'}
                onClick={handleSelectAll}
                disabled={pendingExceptionKeys.length === 0 || showLoader}
                sx={
                  allPendingSelected
                    ? {
                      backgroundColor: colors.grey[400],
                      color: 'black',
                      '&:hover': { backgroundColor: colors.grey[500] },
                    }
                    : {}
                }>
                {allPendingSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
            <Box width={{ xs: '100%', md: '200px' }}>
              <Button
                fullWidth
                variant="contained"
                disabled={selectedCount === 0 || showLoader}
                onClick={() => setAckDialogOpen(true)}>
                {`Acknowledge (${selectedCount})`}
              </Button>
            </Box>
          </Stack>
        }/>

      <StyledModalV2
        open={ackDialogOpen}
        onClose={() => setAckDialogOpen(false)}
        content={
          <Stack gap={2}>
            <Typography variant="h4" textAlign="center">
              Please confirm that you wish to accept {selectedCount} exceptions
              selected
            </Typography>
          </Stack>
        }
        actions={
          <Stack
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="center"
            gap={1}>
            <Box width={{ xs: '100%', md: '180px' }}>
              <Button
                fullWidth
                color="error"
                disabled={selectedCount === 0}
                variant="outlined"
                onClick={() => setAckDialogOpen(false)}>
                Cancel
              </Button>
            </Box>
            <Stack flexDirection="row" gap={2}>
              <Box width={{ xs: '100%', md: '180px' }}>
                <Button
                  fullWidth
                  color="primary"
                  disabled={selectedCount === 0 || showLoader}
                  variant="contained"
                  onClick={() => {
                    onAcknowledge(selectedExceptionKeys)
                    setAckDialogOpen(false)
                    setSelectedExceptionKeys([])
                  }}>
                  Confirm
                </Button>
              </Box>
            </Stack>
          </Stack>
        }/>

      {/* Modal */}
      <Backdrop open={showLoader} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={Boolean(selectedItem)}
        autoSize
        size="md"
        title={selectedItem ? `${selectedItem.exception} ` : ''}
        onClose={handleDialogClose}
        sx={{ maxWidth: 1198 }}>
        <Grid container>
          <Grid item xs={12}>
            {selectedItem && (
              <ExceptionForm
                key={toExceptionKey(selectedItem)}
                data={selectedItem}
                onClose={onClose}
                onRegisterCloseHandler={handleRegisterCloseHandler}
                onSetPending={onSetPending}
                onSaveNote={onSaveNote}
                fetchNotes={fetchNotes}
                onUpdateNote={onUpdateNote}
                onDeleteNote={onDeleteNote}
                onAcknowledge={onAcknowledge}
                processingAck={showLoader}
                onNavigateException={handleNavigateException}/>
            )}
          </Grid>
        </Grid>
      </Dialog>
    </>
  )
}

List.propTypes = {
  account: PropTypes.string,
  model: PropTypes.object.isRequired,
  onAcknowledge: PropTypes.func.isRequired,
  onSetPending: PropTypes.func.isRequired,
}

const mapStateToProps = ({ reportExceptions, settings }) => ({
  account: settings?.account,
  model: reportExceptions,
})

const mapDispatchToProps = (dispatch) => ({
  onSaveNote: (noteData, refetchParams) => {
    dispatch(
      saveExceptionNote(noteData, {
        refetchParams: refetchParams || {
          agreementNumber: noteData.agreementNumber,
          transactionDate: noteData.transactionDate,
          transactionTime: noteData.transactionTime,
          cardnumber: noteData.cardnumber,
          exception: noteData.exception,
        },
      })
    )
  },
  onAcknowledge: (exceptionKeys, status = 'Reviewed') => {
    const exceptions = exceptionKeys
      .map(parseExceptionKey)
      .filter(hasCompleteExceptionIdentity)
    const payload = { exceptions, status }
    dispatch(
      acknowledgeExceptions(payload, {
        updatedExceptionKeys: exceptionKeys,
        updatedExceptionStatus: status,
      })
    )
  },
  onSetPending: (exceptionKeys) => {
    const exceptions = exceptionKeys
      .map(parseExceptionKey)
      .filter(hasCompleteExceptionIdentity)
    const payload = { exceptions, status: 'Pending review' }
    dispatch(
      acknowledgeExceptions(payload, {
        updatedExceptionKeys: exceptionKeys,
        updatedExceptionStatus: 'Pending review',
      })
    )
  },
  fetchNotes: (data) => {
    if (
      data?.agreementNumber &&
      data?.cardnumber &&
      data?.exception &&
      data?.transactionDate &&
      data?.transactionTime
    ) {
      dispatch(
        getExceptionNotes(null, {
          queryParams: {
            agreementNumber: data.agreementNumber,
            transactionDate: data.transactionDate,
            transactionTime: data.transactionTime,
            cardnumber: data.cardnumber,
            exception: data.exception,
          },
        })
      )
    }
  },
  onUpdateNote: (noteId, noteText, refetchParams) => {
    dispatch(
      updateExceptionNote(
        { noteId, noteText },
        {
          refetchParams,
        }
      )
    )
  },
  onDeleteNote: (noteId, refetchParams) => {
    dispatch(
      deleteExceptionNote(
        { noteId },
        {
          refetchParams,
        }
      )
    )
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(List)
